import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AnalyticsEventsService } from '@techspert-io/analytics';
import { CognitoAuthService } from '@techspert-io/auth';
import { ExpertUpdateType, ExpertsService } from '@techspert-io/experts';
import { OpportunityNavigationService } from '@techspert-io/navigation';
import { IOpportunity } from '@techspert-io/opportunities';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, catchError, switchMap, takeWhile, tap } from 'rxjs';
import { UserPermission } from '../../../../features/auth/models/permissions';
import { IExpert } from '../../../../shared/models/expert.interface';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TimezonePreferenceService } from '../../../../shared/services/timezone-preference.service';
import { ExpertStoreService } from '../../../../shared/state/expert.service';
import {
  AskExpertFollowUpQuestionsDialogComponent,
  IAskExpertFollowUpQuestionsDialogData,
  IAskExpertFollowUpQuestionsDialogResult,
} from './dialogs/ask-expert-followup-questions/ask-expert-followup-questions-dialog.component';

interface IColumn {
  name: string;
  key: keyof IExpert;
}

type ExpertsExpanded = Record<string, boolean>;

@Component({
  selector: 'app-expert-table',
  templateUrl: './expert-table.component.html',
  styleUrls: ['./expert-table.component.scss'],
  standalone: false,
})
export class ExpertTableComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() experts: IExpert[];
  @Input() expertLoadingIds: Record<string, boolean>;
  @Input() viewStage: string = '';
  @Input() opportunity: IOpportunity;
  @Input() isExpertApprovalRequired: boolean;

  @ViewChild(MatSort) sort: MatSort;

  get dateSortColumn(): IColumn {
    return ['accepted', 'completed'].includes(this.viewStage)
      ? { name: 'Call Date & Time', key: 'callTime' }
      : { name: 'Date Modified', key: 'lastUpdated' };
  }

  get expertColumns(): IColumn[] {
    const screeningStatusColumn =
      this.viewStage !== 'declined'
        ? [{ name: '', key: 'screeningStatus' as const }]
        : [];

    const availabilityRequestColumn =
      this.viewStage === 'new'
        ? [
            {
              name: '',
              key: 'requestedAnotherTime' as const,
            },
          ]
        : [];

    const pricingColumn =
      this.opportunity.startDate &&
      new Date(this.opportunity.startDate) >= new Date('2023-01-01')
        ? [{ name: '', key: 'unitsPerHour' as const }]
        : [];

    return [
      { name: 'Geography', key: 'geographicTarget' },
      ...screeningStatusColumn,
      ...availabilityRequestColumn,
      { name: 'Expert Name', key: 'firstName' },
      ...pricingColumn,
      { name: 'Profile Type', key: 'profileType' },
      this.dateSortColumn,
    ];
  }

  get columnsToDisplay(): string[] {
    return [
      'dropDown',
      ...(this.isExpertApprovalRequired ? ['approval'] : []),
      ...this.expertColumns.map((c) => c.key),
      'actions',
      'favourited',
    ];
  }

  /** For test control only */
  set isDialogOpen(state: boolean) {
    this.dialogOpen = state;
  }

  expertsFiltered = new MatTableDataSource<IExpert>();

  /** Keeps rows, and their open tab and focus, when an expert is updated */
  readonly trackByExpertId = (_: number, expert: IExpert) => expert.expertId;
  showCollapsed = true;

  navState$: Observable<ExpertsExpanded> = EMPTY;

  readonly selectedTimezone = this.timezonePreferenceService.selectedTimezone;

  get canMarkExpertsAsSeen() {
    return this.cognitoAuthService.loggedInUser?.permissions.has(
      UserPermission.ExpertsMarkSeen
    );
  }

  private dialogOpen = false;

  constructor(
    private opportunityNavigationService: OpportunityNavigationService,
    private expertStoreService: ExpertStoreService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private analyticsEventsService: AnalyticsEventsService,
    private cognitoAuthService: CognitoAuthService,
    private toastr: ToastrService,
    private expertsService: ExpertsService,
    private timezonePreferenceService: TimezonePreferenceService
  ) {}

  ngOnInit(): void {
    this.navState$ = this.opportunityNavigationService.state$.pipe(
      tap(
        (res) =>
          (this.showCollapsed = !this.experts
            .map((e) => res[e.expertId])
            .every((e) => e))
      )
    );

    this.expertsFiltered.data = this.experts;
  }

  ngAfterViewInit(): void {
    this.expertsFiltered.sort = this.sort;
  }

  ngOnChanges(): void {
    this.expertsFiltered.data = this.experts;
  }

  hideExpertDataValue(key: keyof IExpert) {
    return ['unitsPerHour', 'screeningStatus', 'requestedAnotherTime'].includes(
      key
    );
  }

  onToggleExpandAll(expertsExpanded: ExpertsExpanded): void {
    const showCollapsed = !this.experts
      .map((e) => expertsExpanded[e.expertId])
      .every((e) => e);

    const expertsToExpand = this.experts.reduce(
      (prev, expert) => ({
        ...prev,
        [expert.expertId]: showCollapsed,
      }),
      {}
    );

    this.opportunityNavigationService.setNavigationState(expertsToExpand);

    this.experts
      .filter((e) => !e.seenByClient && this.canMarkExpertsAsSeen)
      .forEach(({ expertId }) => this.emitSeenByClientUpdate({ expertId }));
  }

  onToggleExpand(
    { expertId, seenByClient }: IExpert,
    expertsExpanded: ExpertsExpanded
  ): void {
    this.opportunityNavigationService.setNavigationState({
      [expertId]: !!!expertsExpanded[expertId],
    });

    if (!seenByClient && this.canMarkExpertsAsSeen) {
      this.emitSeenByClientUpdate({ expertId });
    }
  }

  onToggleFavourited(e: Event, expert: IExpert): void {
    e.stopPropagation();

    this.expertStoreService.updateExpert({
      expertId: expert.expertId,
      type: ExpertUpdateType.FavouriteExpert,
      payload: { favourited: !expert.favourited },
    });
  }

  askFollowUpQuestions(defaultExpertId?: string) {
    if (this.dialogOpen) {
      return;
    }

    this.dialogOpen = true;

    this.expertsService
      .getFollowUpQuestionPrices()
      .pipe(
        switchMap((prices) => {
          if (!prices.length) {
            this.toastr.error(
              'Something went wrong. Please try again later.',
              'Expert follow-up questions'
            );
            this.dialogOpen = false;
            return EMPTY;
          }

          return this.dialog
            .open<
              AskExpertFollowUpQuestionsDialogComponent,
              IAskExpertFollowUpQuestionsDialogData,
              IAskExpertFollowUpQuestionsDialogResult
            >(AskExpertFollowUpQuestionsDialogComponent, {
              width: '800px',
              minHeight: '500px',
              autoFocus: false,
              data: {
                defaultSelectedExpertIds: defaultExpertId
                  ? [defaultExpertId]
                  : [],
                expertOptions: this.experts
                  .filter((e) => e.expertId === defaultExpertId)
                  .map((expert) => ({
                    expertId: expert.expertId,
                    expertName: `${expert.firstName} ${expert.lastName}`,
                  }))
                  .concat(
                    this.experts
                      .filter((e) => e.connectPhase === 'completed')
                      .filter((expert) => expert.expertId !== defaultExpertId)
                      .map((expert) => ({
                        expertId: expert.expertId,
                        expertName: `${expert.firstName} ${expert.lastName}`,
                      }))
                      .sort((a, b) => a.expertName.localeCompare(b.expertName))
                  ),
                prices,
              },
            })
            .afterClosed()
            .pipe(
              tap(() => {
                this.dialogOpen = false;
              }),
              takeWhile((res) => !!res),
              switchMap((res) =>
                this.notificationService.askExpertsFollowUpQuestions({
                  ...res,
                  opportunityId: this.opportunity.opportunityId,
                })
              ),
              tap(() =>
                this.analyticsEventsService.sendEvent('click', {
                  event_category: 'expert_follow_up_questions',
                  groupId: this.opportunity.opportunityId,
                })
              ),
              tap(() =>
                this.toastr.success(
                  'Thank you - your request has been shared with the Techspert team',
                  'Expert follow-up questions'
                )
              ),
              catchError(() => {
                this.toastr.error(
                  'There was an error submitting your request. Please try again later',
                  'Expert follow-up questions'
                );
                return EMPTY;
              })
            );
        })
      )
      .subscribe();
  }

  private emitSeenByClientUpdate(
    expert: Partial<IExpert> & { expertId: string }
  ): void {
    this.expertStoreService.updateExpert({
      expertId: expert.expertId,
      type: ExpertUpdateType.SeenByClient,
    });
  }
}
